import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { CONSTANT } from "src/app/constant";
import { CandidateRole } from "src/app/models/enum";
import { DisplayObDraft, OBAttempt, OBComments } from "src/app/models/models";

/** Parse codes like "kno_1" or "KNO1" → { cat: "KNO", num: 1 } */
export function parseObsCode(raw?: string): { cat: string; num: number | null } {
	const s = (raw ?? "").trim();
	const m = s.match(/^([a-zA-Z]+(?:[_\s-][a-zA-Z]+)*)[_\s-]*([0-9]+)?$/);
	return { cat: (m?.[1] ?? "").toUpperCase(), num: m?.[2] ? parseInt(m[2], 10) : null };
}

/** Parse labels like "KNO 1" → { cat: "KNO", num: 1 } */
export function parseObLabel(raw?: string): { cat: string; num: number | null } {
	const s = (raw ?? "").trim();
	const m = s.match(/^([a-zA-Z]+(?:[_\s-][a-zA-Z]+)*)[_\s-]*([0-9]+)?$/);
	return { cat: (m?.[1] ?? "").toUpperCase(), num: m?.[2] ? parseInt(m[2], 10) : null };
}

/** Default observation state for a single OB cell. */
export function makeObs(fixedManeuverCode: string): OBAttempt {
	return {
		totalCount: 0,
		currentCount: 0,
		preDefined: false,
		lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
		maneuverCode: fixedManeuverCode,
	};
}

/** Pull category code from an observation record. */
export function categoryFrom(observation: { name?: string; observation_category?: { code?: string } }): string {
	return (observation?.observation_category?.code ?? "").toUpperCase();
}

/**
 * Build a structure keyed by category (KNO/PRO/COM) → keyed by "KNO 1" → OBAttempt.
 * This is used to prepare the initial template for a maneuver.
 */
export function buildCategoriesFromData(
	observations: Array<{ name?: string; observation_category?: { code?: string }; fixedManeuver?: any }>,
	fixedManuverCode: string = "",
): Record<string, Record<string, OBAttempt>> {
	const cats: Record<string, Record<string, OBAttempt>> = {};

	for (const o of observations ?? []) {
		const cat = categoryFrom(o);
		if (!cat) continue;
		const key = (o.name ?? "").trim();
		if (!key) continue;
		cats[cat] ??= {};
		if (!cats[cat][key]) cats[cat][key] = makeObs(o?.fixedManeuver?.fixed_maneuver);
	}
	return cats;
}

/** Prefer "KNO 1", but some places use "KNO1". */
export function obKey(cat: string, num: number, preferSpace = true): string {
	return preferSpace ? `${cat} ${num}` : `${cat}${num}`;
}

/**
 * From a category template object, build a FormGroup of sections with OB controls.
 * The template is the data shape returned by buildCategoriesFromData, without "maneuver".
 */
export function buildSectionsFromTemplate(
	fb: UntypedFormBuilder,
	template: Record<string, unknown>,
	maneuverCode: string = "",
): Record<string, UntypedFormGroup> {
	const sections: Record<string, UntypedFormGroup> = {};
	for (const [sectionName, items] of Object.entries(template)) {
		if (!items || typeof items !== "object") continue;

		const itemKeys = Object.keys(items as Record<string, any>);
		const ctrls = itemKeys.reduce((acc: Record<string, UntypedFormControl>, key) => {
			acc[key] = fb.control({
				value: {
					totalCount: 0,
					currentCount: 0,
					preDefined: false,
					lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
					fixedManeuverCode: maneuverCode,
				},
				disabled: false,
			});
			return acc;
		}, {});
		sections[sectionName] = fb.group(ctrls);
	}
	return sections;
}

export type ObRoleSlot = "pf" | "pm";

function emptyObAttempt(maneuverCode: string): OBAttempt {
	return {
		totalCount: 0,
		currentCount: 0,
		preDefined: false,
		lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
		fixedManeuverCode: maneuverCode,
	};
}

/** TS-1 v2 rating: each OB cell nests pf + pm controls (matches admin maneuver form). */
export function buildSectionsFromTemplateV2(
	fb: UntypedFormBuilder,
	template: Record<string, unknown>,
	maneuverCode: string = "",
): Record<string, UntypedFormGroup> {
	const sections: Record<string, UntypedFormGroup> = {};
	for (const [sectionName, items] of Object.entries(template)) {
		if (!items || typeof items !== "object") continue;

		const itemKeys = Object.keys(items as Record<string, unknown>);
		const obGroups: Record<string, UntypedFormGroup> = {};
		for (const key of itemKeys) {
			obGroups[key] = fb.group({
				pf: fb.control({ value: emptyObAttempt(maneuverCode), disabled: false }),
				pm: fb.control({ value: emptyObAttempt(maneuverCode), disabled: false }),
			});
		}
		sections[sectionName] = fb.group(obGroups);
	}
	return sections;
}

export function resolveV2ObRoleSlot(role?: CandidateRole | string | null): ObRoleSlot | null {
	if (!role) return null;
	const normalized = role.toString().trim().toLowerCase();
	if (normalized === CandidateRole.PF || normalized === "pf") return "pf";
	if (normalized === CandidateRole.PM || normalized === "pm") return "pm";
	return null;
}

export function crewRoleToObSlot(role: CandidateRole | undefined): ObRoleSlot {
	return role === CandidateRole.PM ? "pm" : "pf";
}

/** Resolve flat (legacy) or nested pf/pm OB control on a maneuver form group. */
export function getObCellControl(
	obsGroup: UntypedFormGroup,
	cat: string,
	num: number,
	roleSlot?: ObRoleSlot | null,
): UntypedFormControl | null {
	const catGroup = obsGroup.get(cat) as UntypedFormGroup | null;
	if (!catGroup) return null;

	const key1 = obKey(cat, num, true);
	const key2 = obKey(cat, num, false);
	const key3 = `${cat}${num}`;
	const obCell = (catGroup.get(key1) ?? catGroup.get(key2) ?? catGroup.get(key3)) as UntypedFormGroup | UntypedFormControl | null;
	if (!obCell) return null;

	if (obCell instanceof UntypedFormGroup && (obCell.get("pf") || obCell.get("pm"))) {
		if (roleSlot === "pf") return obCell.get("pf") as UntypedFormControl;
		if (roleSlot === "pm") return obCell.get("pm") as UntypedFormControl;
		return (obCell.get("pf") ?? obCell.get("pm")) as UntypedFormControl;
	}

	return obCell as UntypedFormControl;
}

/** addRemoveOb shape used for rating / preview filtering (includes reset + rating fields). */
export type ObEventForResetFilter = {
	candidateId: string;
	maneuverCode: string;
	observationCode: string;
	createdAt: string;
	isRepeat?: boolean;
	isReset?: boolean;
	isRemoved?: boolean | null;
	isDisabled?: boolean;
	totalAttempt?: number;
	activeAttempt?: number;
	observationCategory?: string;
	candidateRole?: CandidateRole;
	/** Standalone observation note — excluded from OB count / undo replay */
	isObservationComment?: boolean;
	description?: string;
};

export function obEventKey(maneuverCode: string, observationCode: string): string {
	return `${maneuverCode}::${observationCode}`;
}

/** Latest per-OB reset time from entries with `isReset: true`. */
export function buildLastObResetTimeMap(
	addRemoveOb: ObEventForResetFilter[],
	candidateId: string,
): Map<string, number> {
	const map = new Map<string, number>();
	for (const o of addRemoveOb ?? []) {
		if (o.candidateId !== candidateId || !o.isReset) continue;
		const key = obEventKey(o.maneuverCode, o.observationCode);
		const ts = new Date(o.createdAt).getTime();
		const prev = map.get(key) ?? 0;
		if (ts > prev) map.set(key, ts);
	}
	return map;
}

/**
 * Active addRemoveOb rows for rating: excludes repeat-stale rows and any event
 * before the latest per-OB reset (isReset), so calculation uses restored counts.
 *
 * Used by OfflineRatingService.calculate() and TsRatingFormV2Base spiderweb preview.
 * Pair with buildLastObResetTimeMap. selectedOBs is not an input to rating calc.
 */
export function filterAddRemoveObForRating(
	addRemoveOb: ObEventForResetFilter[],
	candidateId: string,
	lastObResetTimeMap: Map<string, number>,
): ObEventForResetFilter[] {
	return (addRemoveOb ?? []).filter((o) => {
		if (o.isObservationComment) return false;
		if (o.candidateId !== candidateId || (o.isRepeat ?? false)) return false;
		const lastObReset = lastObResetTimeMap.get(obEventKey(o.maneuverCode, o.observationCode));
		if (!lastObReset) return true;
		return new Date(o.createdAt).getTime() >= lastObReset;
	});
}

/** True when the observable OB state differs from the captured initial snapshot. */
export function obAttemptDiffersFromInitial(current: OBAttempt, initial: OBAttempt | undefined): boolean {
	if (!initial) return false;
	return (
		(current.totalCount ?? 0) !== (initial.totalCount ?? 0) ||
		(current.currentCount ?? 0) !== (initial.currentCount ?? 0) ||
		!!current.preDefined !== !!initial.preDefined ||
		coerceBool(current.isDisabled, false) !== coerceBool(initial.isDisabled, false)
	);
}

/** Active addRemoveOb rows for one OB, oldest first. */
export function getActiveObEvents(
	addRemoveOb: ObEventForResetFilter[],
	candidateId: string,
	maneuverCode: string,
	observationCode: string,
): ObEventForResetFilter[] {
	return (addRemoveOb ?? [])
		.filter(
			(o) =>
				o.candidateId === candidateId &&
				o.maneuverCode === maneuverCode &&
				o.observationCode === observationCode &&
				!(o.isRepeat ?? false) &&
				!o.isObservationComment,
		)
		.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
}

/** Latest maneuver-repeat timestamp for a candidate+maneuver (`resetManeuvers`). */
export function getLastManeuverRepeatTime(
	resetManeuvers: Array<{ candidateId: string; maneuverCode: string; createdAt: string }>,
	candidateId: string,
	maneuverCode: string,
): number | undefined {
	let latest = 0;
	for (const rm of resetManeuvers ?? []) {
		if (rm.candidateId !== candidateId || rm.maneuverCode !== maneuverCode) continue;
		const ts = new Date(rm.createdAt).getTime();
		if (ts > latest) latest = ts;
	}
	return latest > 0 ? latest : undefined;
}

/** Roles that had a maneuver repeat (from isRepeat markers on draft rows). */
export function getRepeatedRolesForManeuver(
	candidateId: string,
	maneuverCode: string,
	selectedOBs: Array<{ candidateId?: string; maneuverCode?: string; isRepeat?: boolean; candidateRole?: CandidateRole }>,
	addRemoveOb: ObEventForResetFilter[],
	hasManeuverRepeat: boolean,
): CandidateRole[] {
	const roles = new Set<CandidateRole>();
	for (const e of addRemoveOb) {
		if (e.candidateId !== candidateId || e.maneuverCode !== maneuverCode || !(e.isRepeat ?? false)) continue;
		if (e.candidateRole && e.candidateRole !== CandidateRole.NONE) roles.add(e.candidateRole);
	}
	for (const row of selectedOBs) {
		if (row.candidateId !== candidateId || row.maneuverCode !== maneuverCode || !(row.isRepeat ?? false)) continue;
		if (row.candidateRole && row.candidateRole !== CandidateRole.NONE) roles.add(row.candidateRole);
	}
	if (roles.size === 0 && hasManeuverRepeat) {
		return [CandidateRole.PF, CandidateRole.PM];
	}
	return [...roles];
}

/** Whether a draft addRemoveOb row should be replayed onto the form (respects isRepeat + resetManeuvers cut). */
export function shouldReplayDraftAddRemoveObEvent(
	event: ObEventForResetFilter,
	resetManeuvers: Array<{ candidateId: string; maneuverCode: string; createdAt: string }>,
	addRemoveOb: ObEventForResetFilter[],
	roleScopedRepeat: boolean,
): boolean {
	if (event.isRepeat) return false;

	const lastRepeat = getLastManeuverRepeatTime(resetManeuvers, event.candidateId, event.maneuverCode);
	if (lastRepeat == null) return true;

	if (!roleScopedRepeat) {
		return new Date(event.createdAt).getTime() >= lastRepeat;
	}

	const repeatedRoles = getRepeatedRolesForManeuver(event.candidateId, event.maneuverCode, [], addRemoveOb, true);
	if (repeatedRoles.length === 0) return true;

	const eventRole = event.candidateRole;
	if (!eventRole || eventRole === CandidateRole.NONE) {
		return new Date(event.createdAt).getTime() >= lastRepeat;
	}

	if (!repeatedRoles.includes(eventRole)) return true;

	return new Date(event.createdAt).getTime() >= lastRepeat;
}

/** After draft replay, restore repeated pf/pm columns from session preselection baseline. */
export function reapplyV3ManeuverRepeatFormState(
	observations: UntypedFormArray,
	resetManeuvers: Array<{ candidateId: string; maneuverCode: string }>,
	selectedOBs: OBComments[],
	addRemoveOb: ObEventForResetFilter[],
): void {
	const maneuverRepeatCount = new Map<string, number>();
	for (const rm of resetManeuvers) {
		const key = `${rm.candidateId}.${rm.maneuverCode}`;
		maneuverRepeatCount.set(key, (maneuverRepeatCount.get(key) ?? 0) + 1);
	}

	for (let mi = 0; mi < observations.length; mi++) {
		const form = observations.at(mi) as UntypedFormGroup;
		const candidateId = form.get("candidateId")?.value as string;
		const maneuverCode = form.get("maneuverCode")?.value as string;
		if (!candidateId || !maneuverCode) continue;

		const key = `${candidateId}.${maneuverCode}`;
		if ((maneuverRepeatCount.get(key) ?? 0) <= 0) continue;

		const baseline = getManeuverRepeatBaseline(form);
		if (!baseline) continue;

		const roles = getRepeatedRolesForManeuver(candidateId, maneuverCode, selectedOBs, addRemoveOb, true);
		for (const role of roles) {
			const slot = resolveV2ObRoleSlot(role);
			if (slot) restoreManeuverFormRoleFromSnapshot(form, baseline, slot);
		}
	}
}

/**
 * Collapse paired current-attempt N/A + Undo N/A events and ignore Maneuver Outcome
 * cascade N/A rows so per-OB Undo (`restart_alt`) reflects remaining user +/- actions.
 */
function collapseUndoableObNaEvents(events: ObEventForResetFilter[]): ObEventForResetFilter[] {
	const stack: ObEventForResetFilter[] = [];
	for (const event of events) {
		const description = event.description ?? "";
		if (
			description === CONSTANT.MANEUVER_OUTCOME_NA_CASCADE_NOTES ||
			description === CONSTANT.RESET_MANEUVER_OUTCOME_NA_CASCADE_NOTES
		) {
			continue;
		}
		if (description === CONSTANT.RESET_NOT_APPLICABLE_NOTES) {
			for (let i = stack.length - 1; i >= 0; i--) {
				const prev = stack[i];
				if (!obEventMatchesRole(prev, event.candidateRole)) continue;
				if (coerceBool(prev.isDisabled, false) || prev.description === CONSTANT.NOT_APPLICABLE_NOTES) {
					stack.splice(i, 1);
					break;
				}
			}
			continue;
		}
		stack.push(event);
	}
	return stack;
}

/**
 * User actions that can still be undone for one OB (oldest first).
 * Excludes `isReset` audit rows, cancelled N/A pairs, and any event before the latest maneuver repeat.
 */
export function getUndoableObEvents(
	addRemoveOb: ObEventForResetFilter[],
	candidateId: string,
	maneuverCode: string,
	observationCode: string,
	resetManeuvers: Array<{ candidateId: string; maneuverCode: string; createdAt: string }> = [],
): ObEventForResetFilter[] {
	const lastRepeat = getLastManeuverRepeatTime(resetManeuvers, candidateId, maneuverCode);
	const active = getActiveObEvents(addRemoveOb, candidateId, maneuverCode, observationCode).filter((e) => {
		if (e.isReset) return false;
		if (lastRepeat != null && new Date(e.createdAt).getTime() < lastRepeat) return false;
		return true;
	});
	return collapseUndoableObNaEvents(active);
}

/** True when at least one undoable user action exists for this OB. */
export function obCanUndoOneStep(events: ObEventForResetFilter[]): boolean {
	return events.length > 0;
}

export function lastActionFromObEvent(event: ObEventForResetFilter): string {
	if (event.isReset) return CONSTANT.OB_LAST_ACTION.RESET;
	if (coerceBool(event.isDisabled, false)) return CONSTANT.OB_LAST_ACTION.DISABLED;
	if (event.isRemoved === true) return CONSTANT.OB_LAST_ACTION.REMOVED;
	if (event.isRemoved === false) return CONSTANT.OB_LAST_ACTION.ADDED;
	return CONSTANT.OB_LAST_ACTION.NO_ACTION;
}

/** Last instructor add/remove color for a cell, ignoring N/A cascade / RESET cascade rows. */
export function lastUserObLastAction(
	events: ObEventForResetFilter[],
	cell: ObEventForResetFilter,
	fallback = CONSTANT.OB_LAST_ACTION.NO_ACTION,
): string {
	const cellTime = new Date(cell.createdAt).getTime();
	let last = fallback;
	for (const event of events ?? []) {
		if (event.candidateId !== cell.candidateId) continue;
		if (event.maneuverCode !== cell.maneuverCode) continue;
		if (event.observationCode !== cell.observationCode) continue;
		if ((event.candidateRole ?? "") !== (cell.candidateRole ?? "")) continue;
		if (new Date(event.createdAt).getTime() >= cellTime) continue;
		if (event.isObservationComment || event.isReset) continue;
		const description = event.description ?? "";
		if (
			description === CONSTANT.MANEUVER_OUTCOME_NA_CASCADE_NOTES ||
			description === CONSTANT.RESET_MANEUVER_OUTCOME_NA_CASCADE_NOTES
		) {
			continue;
		}
		if (event.isRemoved === true) last = CONSTANT.OB_LAST_ACTION.REMOVED;
		else if (event.isRemoved === false) last = CONSTANT.OB_LAST_ACTION.ADDED;
	}
	return last;
}

export function obAttemptFromEvent(event: ObEventForResetFilter): Partial<OBAttempt> {
	return {
		totalCount: event.totalAttempt ?? 0,
		currentCount: event.activeAttempt ?? 0,
		isDisabled: coerceBool(event.isDisabled, false) ?? false,
	};
}

/** State after undoing the latest event only (not full reset to session start). */
export function computeObAttemptAfterUndoOneStep(
	events: ObEventForResetFilter[],
	initial: OBAttempt | undefined,
): OBAttempt | null {
	if (events.length === 0) return null;

	if (events.length >= 2) {
		const previousEvent = events[events.length - 2];
		return {
			...(initial ?? makeObs("")),
			...obAttemptFromEvent(previousEvent),
			preDefined: initial?.preDefined ?? false,
			lastAction: lastActionFromObEvent(previousEvent),
		} as OBAttempt;
	}

	if (!initial) {
		return {
			totalCount: 0,
			currentCount: 0,
			preDefined: false,
			isDisabled: false,
			lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
			maneuverCode: "",
		};
	}

	return {
		...structuredClone(initial),
		isDisabled: coerceBool(initial.isDisabled, false) ?? false,
		lastAction: initial.lastAction ?? CONSTANT.OB_LAST_ACTION.NO_ACTION,
	};
}

/** PF/PM for undo summary — role of the OB state being restored to (not the removed action). */
export function resolveUndoCommentCandidateRole(
	activeEvents: ObEventForResetFilter[],
	selectedOBs: Array<{
		candidateId?: string;
		maneuverCode?: string;
		observationCode?: string;
		candidateRole?: CandidateRole;
		isReset?: boolean;
		isRepeat?: boolean;
	}>,
	candidateId: string,
	maneuverCode: string,
	observationCode: string,
	removedSelectedIdx: number,
): CandidateRole {
	if (activeEvents.length >= 2) {
		const previousEvent = activeEvents[activeEvents.length - 2];
		if (previousEvent.candidateRole && previousEvent.candidateRole !== CandidateRole.NONE) {
			return previousEvent.candidateRole;
		}
	}

	const remainingSelected =
		removedSelectedIdx >= 0 ? selectedOBs.filter((_, i) => i !== removedSelectedIdx) : selectedOBs;

	for (let i = remainingSelected.length - 1; i >= 0; i--) {
		const entry = remainingSelected[i];
		if (
			entry.candidateId === candidateId &&
			entry.maneuverCode === maneuverCode &&
			entry.observationCode === observationCode &&
			!entry.isReset &&
			!(entry.isRepeat ?? false) &&
			entry.candidateRole &&
			entry.candidateRole !== CandidateRole.NONE
		) {
			return entry.candidateRole;
		}
	}

	return CandidateRole.NONE;
}

export function obDraftStoreKey(candidateId: string, maneuverCode: string, observationCode: string): string {
	return `${candidateId}::${maneuverCode}::${observationCode}`;
}

/** Role-scoped draft key for nested pf/pm OB cells (v2 config). Falls back to flat key when role is absent. */
export function obDraftStoreKeyForRole(
	candidateId: string,
	maneuverCode: string,
	observationCode: string,
	candidateRole?: CandidateRole | null,
): string {
	const roleSlot = resolveV2ObRoleSlot(candidateRole);
	if (roleSlot) {
		return `${candidateId}::${maneuverCode}::${observationCode}::${roleSlot}`;
	}
	return obDraftStoreKey(candidateId, maneuverCode, observationCode);
}

/** Match draft/undo rows to a PF/PM column; legacy rows without role match any column. */
export function obEventMatchesRole(
	event: { candidateRole?: CandidateRole | null },
	candidateRole?: CandidateRole | null,
): boolean {
	if (!candidateRole || candidateRole === CandidateRole.NONE) return true;
	if (!event.candidateRole || event.candidateRole === CandidateRole.NONE) return true;
	return event.candidateRole === candidateRole;
}

/** Role-scoped maneuver repeat — only rows explicitly tagged for the column are reset. */
export function obEventMatchesRepeatScope(
	event: { candidateRole?: CandidateRole | null },
	candidateRole?: CandidateRole | null,
): boolean {
	if (!candidateRole || candidateRole === CandidateRole.NONE) return true;
	if (!event.candidateRole || event.candidateRole === CandidateRole.NONE) return false;
	return event.candidateRole === candidateRole;
}

function hasExplicitCandidateRole(role?: CandidateRole | string | null): boolean {
	return !!role && role !== CandidateRole.NONE;
}

/**
 * TS1-v3 N/A scope — strict PF/PM matching (unlike obEventMatchesRole legacy wildcard).
 * Untagged N/A events only match untagged comments; role-tagged rows require an exact role match.
 */
export function obEventMatchesNaScope(
	event: { candidateRole?: CandidateRole | null },
	commentRole?: CandidateRole | null,
): boolean {
	const eventRole = event.candidateRole;
	const hasEventRole = hasExplicitCandidateRole(eventRole);
	const hasCommentRole = hasExplicitCandidateRole(commentRole);

	if (hasEventRole && hasCommentRole) {
		return eventRole === commentRole;
	}
	if (hasEventRole && !hasCommentRole) {
		return false;
	}
	if (!hasEventRole && hasCommentRole) {
		return false;
	}
	return true;
}

export function getActiveObEventsForRole(
	addRemoveOb: ObEventForResetFilter[],
	candidateId: string,
	maneuverCode: string,
	observationCode: string,
	candidateRole?: CandidateRole | null,
): ObEventForResetFilter[] {
	return getActiveObEvents(addRemoveOb, candidateId, maneuverCode, observationCode).filter((e) =>
		obEventMatchesRole(e, candidateRole),
	);
}

export function getUndoableObEventsForRole(
	addRemoveOb: ObEventForResetFilter[],
	candidateId: string,
	maneuverCode: string,
	observationCode: string,
	resetManeuvers: Array<{ candidateId: string; maneuverCode: string; createdAt: string }> = [],
	candidateRole?: CandidateRole | null,
): ObEventForResetFilter[] {
	return getUndoableObEvents(addRemoveOb, candidateId, maneuverCode, observationCode, resetManeuvers).filter((e) =>
		obEventMatchesRole(e, candidateRole),
	);
}

/**
 * OB state when rehydrating an isReset (undo) row from draft.
 * Matches live `resetObAction` coloring (added/removed), not generic RESET styling.
 */
export function resolveObAttemptFromDraftResetEvent(
	addRemoveOb: ObEventForResetFilter[],
	event: ObEventForResetFilter,
	initialOb: OBAttempt,
	candidateRole?: CandidateRole | null,
): OBAttempt {
	const activeEvents = collapseUndoableObNaEvents(
		getActiveObEventsForRole(
			addRemoveOb,
			event.candidateId,
			event.maneuverCode,
			event.observationCode,
			candidateRole ?? event.candidateRole,
		).filter((e) => new Date(e.createdAt).getTime() <= new Date(event.createdAt).getTime()),
	);
	const restored = computeObAttemptAfterUndoOneStep(activeEvents, initialOb);
	if (restored) {
		return {
			...restored,
			isDisabled: coerceBool(restored.isDisabled, false) ?? false,
		};
	}

	return {
		...structuredClone(initialOb),
		...obAttemptFromEvent(event),
		preDefined: initialOb.preDefined,
		isDisabled: coerceBool(event.isDisabled, false) ?? false,
		lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
	};
}

/** Apply one addRemoveOb draft row to an OB form control (skips isRepeat). */
export function applyDraftAddRemoveObEvent(
	ctrl: { value: OBAttempt; patchValue(v: Partial<OBAttempt>): void },
	event: ObEventForResetFilter & { description?: string },
	addRemoveOb: ObEventForResetFilter[],
	initialObByKey: Record<string, OBAttempt>,
): void {
	if (event.isObservationComment) return;

	const prev = ctrl.value;
	const storeKey = obDraftStoreKeyForRole(
		event.candidateId,
		event.maneuverCode,
		event.observationCode,
		event.candidateRole,
	);
	if (!(storeKey in initialObByKey)) {
		initialObByKey[storeKey] = normalizePredefinedObAttempt(structuredClone(prev));
	}
	if (event.isRepeat) return;

	if (event.isReset || event.description === CONSTANT.RESET_OB_NOTES) {
		ctrl.patchValue(
			resolveObAttemptFromDraftResetEvent(addRemoveOb, event, initialObByKey[storeKey], event.candidateRole),
		);
		return;
	}

	if (event.description === CONSTANT.RESET_MANEUVER_OUTCOME_NA_CASCADE_NOTES) {
		ctrl.patchValue({
			...prev,
			...obAttemptFromEvent(event),
			preDefined: initialObByKey[storeKey].preDefined,
			isDisabled: false,
			lastAction: lastUserObLastAction(
				addRemoveOb,
				event,
				initialObByKey[storeKey].lastAction ?? CONSTANT.OB_LAST_ACTION.NO_ACTION,
			),
		});
		return;
	}

	ctrl.patchValue({
		...prev,
		...obAttemptFromEvent(event),
		preDefined: initialObByKey[storeKey].preDefined,
		isDisabled: coerceBool(event.isDisabled, false) ?? false,
		lastAction: lastActionFromObEvent(event),
	});
}

/** Restore add/remove circle color from last-state displayObs after event replay.
 * Skip `disabled` — N/A greys the cell separately and must not overwrite red/blue. */
export function applyDisplayObsLastActions(
	observations: UntypedFormArray,
	displayObs: DisplayObDraft[] | undefined,
): void {
	for (const row of displayObs ?? []) {
		if (!row.lastAction || row.lastAction === CONSTANT.OB_LAST_ACTION.NO_ACTION) continue;
		if (row.lastAction === CONSTANT.OB_LAST_ACTION.DISABLED) continue;
		if (!row.observationCode || !row.maneuverCode) continue;
		const idx = (observations.value || []).findIndex(
			(item: { maneuverCode?: string; candidateId?: string }) =>
				item.maneuverCode === row.maneuverCode && item.candidateId === row.candidateId,
		);
		if (idx < 0) continue;
		const { cat, num } = parseObsCode(row.observationCode);
		if (!cat || num == null) continue;
		const ctrl = getObCellControl(
			observations.at(idx) as UntypedFormGroup,
			cat,
			num,
			resolveV2ObRoleSlot(row.candidateRole),
		);
		if (!ctrl) continue;
		ctrl.patchValue({ ...ctrl.value, lastAction: row.lastAction });
	}
}

/** Active comment rows for form controls / undo (excludes maneuver-repeat history + undo markers). */
export function filterSelectedOBsForSummary(selectedOBs: OBComments[], candidateId?: string): OBComments[] {
	return (selectedOBs ?? []).filter(
		(s) => !(s.isRepeat ?? false) && !s.isReset && (candidateId == null || s.candidateId === candidateId),
	);
}

/** All persisted comment rows except per-OB undo markers (`isReset`). Keeps `isRepeat` (pre-repeat history). */
export function filterSelectedOBsExcludingReset(
	selectedOBs: OBComments[],
	candidateId?: string,
	maneuverCode?: string,
): OBComments[] {
	return (selectedOBs ?? []).filter((s) => {
		if (s.isReset) return false;
		if (candidateId != null && s.candidateId !== candidateId) return false;
		if (maneuverCode != null && s.maneuverCode !== maneuverCode) return false;
		return true;
	});
}

/**
 * Full maneuver summary for display (live reload): pre-repeat rows, REPEAT marker, post-repeat rows.
 * Preserves draft array order; inserts REPEAT before the first post-repeat row when repeatCount > 0.
 */
export function buildManeuverDisplaySummaryRows(params: {
	selectedOBs: OBComments[];
	candidateId: string;
	maneuverCode: string;
	repeatCount: number;
	maneuverLabel: string;
	isCabinCrewForm?: boolean;
}): OBComments[] {
	const { selectedOBs, candidateId, maneuverCode, repeatCount, maneuverLabel, isCabinCrewForm } = params;
	const rows = filterSelectedOBsExcludingReset(selectedOBs, candidateId, maneuverCode);
	const result: OBComments[] = [];
	let repeatInserted = false;

	for (const row of rows) {
		if (!(row.isRepeat ?? false) && repeatCount > 0 && !repeatInserted) {
			result.push(
				buildManeuverRepeatSummaryComment({
					repeatCount,
					maneuverLabel,
					maneuverCode,
					candidateId,
					isCabinCrewForm,
				}),
			);
			repeatInserted = true;
		}
		result.push(row);
	}

	if (repeatCount > 0 && !repeatInserted) {
		result.push(
			buildManeuverRepeatSummaryComment({
				repeatCount,
				maneuverLabel,
				maneuverCode,
				candidateId,
				isCabinCrewForm,
			}),
		);
	}

	return result;
}

const MANEUVER_FORM_BASE_FIELDS = new Set([
	"assessmentId",
	"candidateId",
	"maneuver",
	"maneuverCode",
	"otherComents",
	"repeat",
	"safetyOutcome",
	"selectedOBs",
	"traineeAssessmentId",
]);

function obAttemptHasActivity(attempt: OBAttempt | null | undefined): boolean {
	if (!attempt) return false;
	const count = attempt.currentCount ?? 0;
	const totalCount = attempt.totalCount ?? 0;
	const isDisabled = !!attempt.isDisabled;
	return !isDisabled && (count !== 0 || totalCount !== 0);
}

function maneuverFormHasObActivity(
	raw: Record<string, unknown>,
	roleSlot?: ObRoleSlot | null,
): boolean {
	for (const [section, items] of Object.entries(raw ?? {})) {
		if (MANEUVER_FORM_BASE_FIELDS.has(section) || !items || typeof items !== "object") continue;
		for (const cell of Object.values(items as Record<string, unknown>)) {
			if (!cell || typeof cell !== "object") continue;
			if ("pf" in cell || "pm" in cell) {
				const nested = cell as { pf?: OBAttempt; pm?: OBAttempt };
				if (roleSlot) {
					if (obAttemptHasActivity(nested[roleSlot])) return true;
				} else if (obAttemptHasActivity(nested.pf) || obAttemptHasActivity(nested.pm)) {
					return true;
				}
			} else if (!roleSlot && obAttemptHasActivity(cell as OBAttempt)) {
				return true;
			}
		}
	}
	return false;
}

/** True when every OB cell on a maneuver form has zero activity (ignores disabled rows). */
export function areManeuverFormObTotalsZero(maneuverForm: UntypedFormGroup): boolean {
	return !maneuverFormHasObActivity(maneuverForm.getRawValue());
}

/** v2 nested pf/pm: true when the given role column has zero OB activity on the maneuver form. */
export function areManeuverFormRoleObTotalsZero(
	maneuverForm: UntypedFormGroup,
	roleSlot: ObRoleSlot,
): boolean {
	return !maneuverFormHasObActivity(maneuverForm.getRawValue(), roleSlot);
}

/** Session repeat baselines survive ob-panel destroy/recreate (maneuver sidebar navigation). */
const ts1V3ManeuverRepeatBaselines = new Map<string, Record<string, unknown>>();

export function maneuverRepeatBaselineKey(form: UntypedFormGroup): string | null {
	const candidateId = form.get("candidateId")?.value;
	const maneuverCode = form.get("maneuverCode")?.value;
	if (!candidateId || !maneuverCode) return null;
	return `${candidateId}.${maneuverCode}`;
}

/** Capture session-start maneuver form state once per candidate + maneuver (legacy tempFrom parity). */
export function captureManeuverRepeatBaselineOnce(form: UntypedFormGroup): Record<string, unknown> | null {
	const key = maneuverRepeatBaselineKey(form);
	if (!key) return null;
	if (!ts1V3ManeuverRepeatBaselines.has(key)) {
		ts1V3ManeuverRepeatBaselines.set(key, structuredClone(form.getRawValue()));
	}
	return ts1V3ManeuverRepeatBaselines.get(key) ?? null;
}

/** Ensure session-start repeat baselines exist; never overwrite once captured. */
export function refreshManeuverRepeatBaselines(ratingForm: UntypedFormArray): void {
	if (!ratingForm?.length) return;
	for (let ci = 0; ci < ratingForm.length; ci++) {
		const obs = ratingForm.at(ci).get("observations") as UntypedFormArray | null;
		if (!obs?.length) continue;
		for (let mi = 0; mi < obs.length; mi++) {
			captureManeuverRepeatBaselineOnce(obs.at(mi) as UntypedFormGroup);
		}
	}
}

export function getManeuverRepeatBaseline(form: UntypedFormGroup): Record<string, unknown> | null {
	const key = maneuverRepeatBaselineKey(form);
	if (!key) return null;
	return ts1V3ManeuverRepeatBaselines.get(key) ?? null;
}

function zeroRoleAttempt(attempt: unknown, maneuverCode: string): OBAttempt {
	const current = attempt && typeof attempt === "object" ? (attempt as OBAttempt) : ({} as OBAttempt);
	return {
		...current,
		totalCount: 0,
		currentCount: 0,
		isDisabled: false,
		lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
		fixedManeuverCode: current.fixedManeuverCode || current.maneuverCode || maneuverCode,
	};
}

/** Zero both PF and PM columns on a cloned maneuver value. Does not write the baseline map. */
export function zeroManeuverFormRoleSnapshot(raw: Record<string, unknown>): Record<string, unknown> {
	const next = structuredClone(raw);
	const maneuverCode = String(next["maneuverCode"] ?? "");
	for (const [section, items] of Object.entries(next)) {
		if (MANEUVER_FORM_BASE_FIELDS.has(section) || !items || typeof items !== "object") continue;
		for (const cell of Object.values(items as Record<string, unknown>)) {
			if (!cell || typeof cell !== "object") continue;
			const nested = cell as { pf?: unknown; pm?: unknown };
			if ("pf" in nested) nested.pf = zeroRoleAttempt(nested.pf, maneuverCode);
			if ("pm" in nested) nested.pm = zeroRoleAttempt(nested.pm, maneuverCode);
		}
	}
	return next;
}

/**
 * Session-start snapshot for Repeat. A missing baseline becomes a zeroed copy of the
 * current form; the rated form itself is not stored.
 */
export function resolveManeuverRepeatSnapshot(form: UntypedFormGroup): Record<string, unknown> | null {
	const existing = getManeuverRepeatBaseline(form);
	if (existing) return existing;
	if (!maneuverRepeatBaselineKey(form)) return null;
	return zeroManeuverFormRoleSnapshot(form.getRawValue() as Record<string, unknown>);
}

/** After repeat, baseline becomes the reset state for the next repeat cycle (legacy tempFrom recapture). */
export function updateManeuverRepeatBaselineAfterRepeat(form: UntypedFormGroup): void {
	const key = maneuverRepeatBaselineKey(form);
	if (!key) return;
	ts1V3ManeuverRepeatBaselines.set(key, structuredClone(form.getRawValue()));
}

export function captureAllManeuverRepeatBaselines(ratingForm: UntypedFormArray): void {
	if (!ratingForm?.length) return;
	for (let ci = 0; ci < ratingForm.length; ci++) {
		const obs = ratingForm.at(ci).get("observations") as UntypedFormArray | null;
		if (!obs?.length) continue;
		for (let mi = 0; mi < obs.length; mi++) {
			captureManeuverRepeatBaselineOnce(obs.at(mi) as UntypedFormGroup);
		}
	}
}

function obNaEventKey(event: {
	candidateId?: string;
	maneuverCode?: string;
	observationCode?: string;
	candidateRole?: CandidateRole | string | null;
}): string {
	return `${event.candidateId ?? ""}::${event.maneuverCode ?? ""}::${event.observationCode ?? ""}::${event.candidateRole ?? ""}`;
}

/** Latest replayable N/A state per OB cell (true = greyed, false = re-enabled). */
export function buildLatestReplayableObNaStateByKey(
	addRemoveOb: Array<ObEventForResetFilter & { description?: string }> = [],
	resetManeuvers: Array<{ candidateId: string; maneuverCode: string; createdAt: string }> = [],
	roleScopedRepeat = true,
	strictRoleScopedNa = false,
): Map<string, boolean> {
	const latest = new Map<string, { at: number; order: number; disabled: boolean }>();
	let order = 0;

	for (const event of addRemoveOb) {
		if (event.isReset || event.isRepeat) {
			order++;
			continue;
		}

		const isNaToggle =
			event.description === CONSTANT.NOT_APPLICABLE_NOTES ||
			event.description === CONSTANT.RESET_NOT_APPLICABLE_NOTES;
		if (!isNaToggle) {
			order++;
			continue;
		}

		if (strictRoleScopedNa && !hasExplicitCandidateRole(event.candidateRole)) {
			order++;
			continue;
		}

		if (!shouldReplayDraftAddRemoveObEvent(event, resetManeuvers, addRemoveOb, roleScopedRepeat)) {
			order++;
			continue;
		}

		const key = obNaEventKey(event);
		const at = event.createdAt ? new Date(event.createdAt).getTime() : order;
		const disabled = event.description === CONSTANT.NOT_APPLICABLE_NOTES;
		const prev = latest.get(key);
		if (!prev || at > prev.at || (at === prev.at && order > prev.order)) {
			latest.set(key, { at, order, disabled });
		}
		order++;
	}

	const result = new Map<string, boolean>();
	for (const [key, { disabled }] of latest) {
		result.set(key, disabled);
	}
	return result;
}

/** After draft replay, ensure OB form controls reflect the latest N/A state from addRemoveOb. */
export function applyLatestNaDisabledStateFromAddRemoveOb(
	observations: UntypedFormArray,
	addRemoveOb: Array<ObEventForResetFilter & { description?: string }>,
	resetManeuvers: Array<{ candidateId: string; maneuverCode: string; createdAt: string }> = [],
	strictRoleScopedNa = false,
): { applied: number; missingControl: number } {
	const latestNa = buildLatestReplayableObNaStateByKey(addRemoveOb, resetManeuvers, true, strictRoleScopedNa);
	let applied = 0;
	let missingControl = 0;

	for (let mi = 0; mi < observations.length; mi++) {
		const form = observations.at(mi) as UntypedFormGroup;
		const candidateId = form.get("candidateId")?.value as string;
		const maneuverCode = form.get("maneuverCode")?.value as string;
		if (!candidateId || !maneuverCode) continue;

		for (const [key, disabled] of latestNa) {
			const [eventCandidateId, eventManeuverCode, observationCode, roleRaw] = key.split("::");
			if (eventCandidateId !== candidateId || eventManeuverCode !== maneuverCode) continue;

			if (strictRoleScopedNa && !roleRaw) continue;

			const { cat, num } = parseObsCode(observationCode);
			if (!cat || num == null) continue;

			const roleSlot = resolveV2ObRoleSlot(roleRaw as CandidateRole);
			const ctrl = getObCellControl(form, cat, num, roleSlot);
			if (!ctrl) {
				missingControl++;
				continue;
			}

			const current = ctrl.value as OBAttempt;
			if (!!current.isDisabled === disabled) continue;

			ctrl.patchValue(
				{
					...current,
					isDisabled: disabled,
					lastAction: disabled
						? CONSTANT.OB_LAST_ACTION.DISABLED
						: current.lastAction === CONSTANT.OB_LAST_ACTION.DISABLED
							? CONSTANT.OB_LAST_ACTION.RESTORE
							: current.lastAction,
				},
				{ emitEvent: false },
			);
			applied++;
		}
	}

	return { applied, missingControl };
}

function parseObCellNum(section: string, cellKey: string): number | null {
	const prefix = section.toUpperCase();
	const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
	const match =
		cellKey.match(new RegExp(`^${escaped}\\s*(\\d+)$`, "i")) ??
		cellKey.match(new RegExp(`^${escaped}(\\d+)$`, "i"));
	return match ? Number(match[1]) : null;
}

function normalizePredefinedObAttempt(value: OBAttempt): OBAttempt {
	if (coerceBool(value.preDefined, false) || value.lastAction !== CONSTANT.OB_LAST_ACTION.PRE_SELECTED) {
		return value;
	}
	return { ...value, preDefined: true };
}

/** Session-start OB baselines for TS-1 v3 undo (captured before draft add/remove replay). */
const ts1V3ObUndoBaselinesByKey = new Map<string, OBAttempt>();

/** Drop v3 undo baselines when leaving a rating session (draft/form navigation). */
export function clearTs1V3ObUndoBaselines(): void {
	ts1V3ObUndoBaselinesByKey.clear();
}

/** Persist pre-replay OB baselines so ob-panel undo survives draft reload / panel recreate. */
export function persistTs1V3ObUndoBaselines(initialObByKey: Record<string, OBAttempt>): void {
	for (const [key, value] of Object.entries(initialObByKey)) {
		if (!ts1V3ObUndoBaselinesByKey.has(key)) {
			ts1V3ObUndoBaselinesByKey.set(key, structuredClone(value));
		}
	}
}

export function getTs1V3ObUndoBaseline(key: string): OBAttempt | undefined {
	const baseline = ts1V3ObUndoBaselinesByKey.get(key);
	return baseline ? structuredClone(baseline) : undefined;
}

export function setTs1V3ObUndoBaseline(key: string, value: OBAttempt): void {
	ts1V3ObUndoBaselinesByKey.set(key, structuredClone(value));
}

export function getTs1V3ObUndoBaselinesRecord(): Record<string, OBAttempt> {
	const record: Record<string, OBAttempt> = {};
	for (const [key, value] of ts1V3ObUndoBaselinesByKey) {
		record[key] = structuredClone(value);
	}
	return record;
}

/** Seed from live forms and persist for v3 undo (call after preselection, before draft replay). */
export function seedAndPersistTs1V3ObUndoBaselines(observations: UntypedFormArray): void {
	const initialObByKey: Record<string, OBAttempt> = {};
	seedInitialObByKeyFromObservationForms(observations, initialObByKey);
	persistTs1V3ObUndoBaselines(initialObByKey);
}

/** Capture session-start OB state before replaying draft add/remove events (preserves preDefined). */
export function seedInitialObByKeyFromObservationForms(
	observations: UntypedFormArray,
	initialObByKey: Record<string, OBAttempt>,
): void {
	for (let mi = 0; mi < observations.length; mi++) {
		const form = observations.at(mi) as UntypedFormGroup;
		const candidateId = form.get("candidateId")?.value as string;
		const maneuverCode = form.get("maneuverCode")?.value as string;
		if (!candidateId || !maneuverCode) continue;

		for (const [section, items] of Object.entries(form.getRawValue())) {
			if (MANEUVER_FORM_BASE_FIELDS.has(section) || !items || typeof items !== "object") continue;

			for (const [cellKey, cellValue] of Object.entries(items as Record<string, unknown>)) {
				if (!cellValue || typeof cellValue !== "object") continue;
				if (!("pf" in cellValue) && !("pm" in cellValue)) continue;

				for (const role of ["pf", "pm"] as const) {
					const snapRole = (cellValue as Record<string, unknown>)[role];
					if (!snapRole || typeof snapRole !== "object") continue;

					const num = parseObCellNum(section, cellKey);
					if (num == null) continue;

					const observationCode = `${section.toLowerCase()}_${num}`;
					const candidateRole = role === "pf" ? CandidateRole.PF : CandidateRole.PM;
					const key = obDraftStoreKeyForRole(candidateId, maneuverCode, observationCode, candidateRole);
					if (!(key in initialObByKey)) {
						initialObByKey[key] = normalizePredefinedObAttempt(structuredClone(snapRole as OBAttempt));
					}
				}
			}
		}
	}
}

/** Restore one pf/pm column from a session-start snapshot; other role cells on the form are unchanged. */
export function restoreManeuverFormRoleFromSnapshot(
	form: UntypedFormGroup,
	snapshot: Record<string, unknown>,
	roleSlot: ObRoleSlot,
): void {
	for (const [section, items] of Object.entries(snapshot)) {
		if (MANEUVER_FORM_BASE_FIELDS.has(section) || !items || typeof items !== "object") continue;

		for (const [cellKey, cellValue] of Object.entries(items as Record<string, unknown>)) {
			if (!cellValue || typeof cellValue !== "object") continue;
			if (!("pf" in cellValue) && !("pm" in cellValue)) continue;

			const snapRole = (cellValue as Record<string, unknown>)[roleSlot];
			if (snapRole === undefined) continue;

			const num = parseObCellNum(section, cellKey);
			if (num == null) continue;

			const roleCtrl = getObCellControl(form, section, num, roleSlot);
			if (!roleCtrl) continue;

			roleCtrl.patchValue(structuredClone(snapRole as OBAttempt), { emitEvent: false });
		}
	}
}

/** Walk every nested pf/pm OB cell for one role column on a maneuver form. */
export function forEachManeuverRoleObCell(
	form: UntypedFormGroup,
	roleSlot: ObRoleSlot,
	fn: (params: {
		section: string;
		num: number;
		obLabel: string;
		observationCode: string;
		control: UntypedFormControl;
		attempt: OBAttempt;
	}) => void,
): void {
	for (const [section, items] of Object.entries(form.getRawValue() ?? {})) {
		if (MANEUVER_FORM_BASE_FIELDS.has(section) || !items || typeof items !== "object") continue;

		for (const [cellKey, cellValue] of Object.entries(items as Record<string, unknown>)) {
			if (!cellValue || typeof cellValue !== "object") continue;
			if (!("pf" in cellValue) && !("pm" in cellValue)) continue;

			const num = parseObCellNum(section, cellKey);
			if (num == null) continue;

			const control = getObCellControl(form, section, num, roleSlot);
			if (!control) continue;

			const attempt = control.value as OBAttempt;
			if (!attempt || typeof attempt !== "object") continue;

			const obLabel = `${section.toUpperCase()} ${num}`;
			const observationCode = `${section.toLowerCase()}_${num}`;
			fn({ section, num, obLabel, observationCode, control, attempt });
		}
	}
}

export function maneuverHasRepeatSummaryHistory(
	selectedOBs: OBComments[],
	candidateId: string,
	maneuverCode: string,
): boolean {
	return (selectedOBs ?? []).some(
		(r) => r.candidateId === candidateId && r.maneuverCode === maneuverCode && (r.isRepeat ?? false),
	);
}

export function buildManeuverRepeatSummaryComment(params: {
	repeatCount: number;
	maneuverLabel: string;
	maneuverCode: string;
	candidateId: string;
	isCabinCrewForm?: boolean;
}): OBComments {
	const { repeatCount, maneuverLabel, maneuverCode, candidateId, isCabinCrewForm } = params;
	const maneuverText =
		repeatCount > 0
			? `R${repeatCount} ${!isCabinCrewForm ? maneuverLabel : ""}`.trim()
			: !isCabinCrewForm
				? maneuverLabel
				: "";

	return {
		maneuver: maneuverText,
		ob: "REPEAT",
		remarks: "REPEAT",
		action: false,
		instructorComment: "",
		candidateId,
		maneuverCode,
		isDisabled: false,
	} as OBComments;
}

export function maneuverRepeatSummaryExists(
	obComments: OBComments[],
	candidateId: string,
	maneuverCode: string,
): boolean {
	return obComments.some((c) => c.ob === "REPEAT" && c.candidateId === candidateId && c.maneuverCode === maneuverCode);
}

export function appendManeuverRepeatSummaryIfNeeded(
	obComments: OBComments[],
	params: {
		repeatCount: number;
		maneuverLabel: string;
		maneuverCode: string;
		candidateId: string;
		isCabinCrewForm?: boolean;
	},
): void {
	if (params.repeatCount <= 0) return;
	if (maneuverRepeatSummaryExists(obComments, params.candidateId, params.maneuverCode)) return;
	appendObCommentToSummary(obComments, buildManeuverRepeatSummaryComment(params));
}

/** Rebuild entire live summary from draft selectedOBs (all maneuvers). */
export function replaceObCommentsFromSync(obComments: OBComments[], syncComments: OBComments[]): void {
	obComments.length = 0;
	for (const val of filterSelectedOBsForSummary(syncComments)) {
		appendObCommentToSummary(obComments, val);
	}
}

/** Rebuild summary rows for one maneuver only (draft reload); leaves other maneuvers untouched. */
export function replaceObCommentsForManeuver(
	obComments: OBComments[],
	maneuverCode: string,
	candidateId: string,
	syncComments: OBComments[],
): void {
	for (let i = obComments.length - 1; i >= 0; i--) {
		const c = obComments[i];
		if (c.candidateId !== candidateId) continue;
		if (c.maneuverCode === maneuverCode || (c.ob === "REPEAT" && c.maneuverCode === maneuverCode)) {
			obComments.splice(i, 1);
		}
	}

	for (const val of syncComments) {
		appendObCommentToSummary(obComments, val);
	}
}

/** Append one summary row with lastActionDisabled derived from prior current-attempt rows for the same OB. */
export function appendObCommentToSummary(obComments: OBComments[], val: OBComments): void {
	let isLastStateDisabled = false;
	const isUndoNaCandidate =
		!val.isDisabled &&
		val.action == null &&
		!val.isObservationComment &&
		!val.isManeuverOutcome &&
		!val.isTemCompetencyOutcome &&
		val.ob !== "REPEAT";

	if (isUndoNaCandidate) {
		for (let i = obComments.length - 1; i >= 0; i--) {
			const comment = obComments[i];
			if (
				val.maneuverCode &&
				comment.maneuverCode === val.maneuverCode &&
				comment.ob === "REPEAT" &&
				(!val.candidateId || !comment.candidateId || comment.candidateId === val.candidateId)
			) {
				break;
			}
			if (Boolean(comment.isRepeat) !== Boolean(val.isRepeat)) continue;
			if (comment.candidateId !== val.candidateId) continue;
			if (comment.maneuverCode !== val.maneuverCode) continue;
			if (!comment.observationCode || comment.observationCode !== val.observationCode) continue;
			if (!obEventMatchesNaScope(comment, val.candidateRole)) continue;
			isLastStateDisabled = !!comment.isDisabled;
			break;
		}
	}
	obComments.push({ ...val, lastActionDisabled: isLastStateDisabled });
}

/**
 * Update live summary from a selectedOBs emit (live edit, draft reload, or per-OB undo).
 * Live undo: remove the latest comment row for that OB only.
 * Draft reload: maneuver-scoped syncComments rebuild (not whole candidate).
 */
export function handleObCommentSummaryEvent(obComments: OBComments[], val: OBComments): boolean {
	if (val.isReset && val.syncComments != null) {
		if (val.maneuverCode && val.candidateId) {
			replaceObCommentsForManeuver(obComments, val.maneuverCode, val.candidateId, val.syncComments);
		} else {
			replaceObCommentsFromSync(obComments, val.syncComments);
		}
		return true;
	}

	if (val.isReset) {
		removeLastObCommentEntryBeforeUndo(obComments, val);
		return true;
	}

	if (val.isRepeat || obComments.includes(val)) {
		return false;
	}

	if (val.ob === "REPEAT" && val.candidateId && val.maneuverCode) {
		if (maneuverRepeatSummaryExists(obComments, val.candidateId, val.maneuverCode)) {
			return false;
		}
	}

	if (val.isObservationComment) {
		replaceObservationCommentInSummary(obComments, val);
		return true;
	}

	appendObCommentToSummary(obComments, val);
	return true;
}

/** Replace the active standalone observation note for the same Maneuver→OBS→role cell. */
export function replaceObservationCommentInSummary(obComments: OBComments[], val: OBComments): void {
	for (let i = obComments.length - 1; i >= 0; i--) {
		const existing = obComments[i];
		if (!existing.isObservationComment) continue;
		if (existing.candidateId !== val.candidateId) continue;
		if (existing.maneuverCode !== val.maneuverCode) continue;
		if ((existing.observationCode ?? "") !== (val.observationCode ?? "")) continue;
		if (!obEventMatchesRole(existing, val.candidateRole)) continue;
		obComments.splice(i, 1);
	}
	appendObCommentToSummary(obComments, val);
}

/** Remove the latest current-attempt non-undo comment row for an OB without crossing a REPEAT divider. */
export function removeLastObCommentEntryBeforeUndo(
	obComments: Array<{
		observationCode?: string;
		maneuverCode?: string;
		candidateId?: string;
		candidateRole?: CandidateRole;
		ob?: string;
		isReset?: boolean;
		isRepeat?: boolean;
		isDisabled?: boolean;
		isObservationComment?: boolean;
		isManeuverOutcome?: boolean;
		isTemCompetencyOutcome?: boolean;
	}>,
	val: {
		observationCode?: string;
		maneuverCode?: string;
		candidateId?: string;
		candidateRole?: CandidateRole;
		ob?: string;
		lastActionDisabled?: boolean;
	},
): void {
	for (let i = obComments.length - 1; i >= 0; i--) {
		const c = obComments[i];
		if (
			val.maneuverCode &&
			c.maneuverCode === val.maneuverCode &&
			c.ob === "REPEAT" &&
			(!val.candidateId || !c.candidateId || c.candidateId === val.candidateId)
		) {
			return;
		}
		if (
			c.isReset ||
			c.isRepeat ||
			c.isObservationComment ||
			c.isManeuverOutcome ||
			c.isTemCompetencyOutcome
		) {
			continue;
		}
		if (val.candidateId && c.candidateId !== val.candidateId) continue;
		if (val.maneuverCode && c.maneuverCode !== val.maneuverCode) continue;
		if (val.candidateRole && !obEventMatchesRole(c, val.candidateRole)) continue;
		if (val.lastActionDisabled && !c.isDisabled) continue;
		if (val.observationCode && c.observationCode === val.observationCode) {
			obComments.splice(i, 1);
			return;
		}
		if (val.ob && c.ob === val.ob) {
			obComments.splice(i, 1);
			return;
		}
	}
}

/** Rebuild live summary from maneuver selectedOBs after draft reload (mirrors add-maneuver-form emit). */
export function syncRatingSummaryFromManeuverForms(
	obComments: OBComments[],
	ratingForm: UntypedFormArray,
	draftSelected: OBComments[],
): void {
	obComments.length = 0;
	if (!ratingForm?.length) return;

	for (let ci = 0; ci < ratingForm.length; ci++) {
		const obs = ratingForm.at(ci).get("observations") as UntypedFormArray | null;
		if (!obs?.length) continue;

		for (let mi = 0; mi < obs.length; mi++) {
			appendManeuverSummaryComments(obComments, obs.at(mi) as UntypedFormGroup, draftSelected);
		}
	}
}

function appendManeuverSummaryComments(
	obComments: OBComments[],
	maneuverForm: UntypedFormGroup,
	draftSelected: OBComments[],
): void {
	const selected = (maneuverForm.get("selectedOBs")?.value ?? []) as OBComments[];
	const candidateId = maneuverForm.get("candidateId")?.value as string;
	const maneuverCode = maneuverForm.get("maneuverCode")?.value as string;
	const repeatCount = Number(maneuverForm.get("repeat")?.value ?? 0);
	const maneuverLabel = (maneuverForm.get("maneuver")?.value ?? "") as string;

	if (!candidateId || !maneuverCode) return;

	if (repeatCount > 0 || maneuverHasRepeatSummaryHistory(draftSelected, candidateId, maneuverCode)) {
		const displayRows = buildManeuverDisplaySummaryRows({
			selectedOBs: draftSelected,
			candidateId,
			maneuverCode,
			repeatCount,
			maneuverLabel,
		});
		handleObCommentSummaryEvent(obComments, {
			isReset: true,
			syncComments: displayRows,
			maneuverCode,
			candidateId,
		} as OBComments);
		return;
	}

	for (const row of filterSelectedOBsForSummary(selected, candidateId)) {
		handleObCommentSummaryEvent(obComments, row);
	}
}

/** Utility: normalize possibly-string booleans from storage to real booleans. */
export function coerceBool(v: any, fallback: boolean | undefined = undefined): boolean | undefined {
	if (typeof v === "boolean") return v;
	if (typeof v === "string") {
		const s = v.trim().toLowerCase();
		if (s === "true" || s === "1" || s === "yes") return true;
		if (s === "false" || s === "0" || s === "no") return false;
	}
	if (typeof v === "number") return v === 1;
	return fallback;
}
